import React from 'react';
import { NavLink } from 'react-router-dom';
import { navSections } from './navigation';
import { IconClose, IconPanelLeft } from './icons';

const NavItem = ({ item, collapsed, onNavigate }) => {
  const { to, label, icon: Icon } = item;
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `group relative flex items-center rounded-lg text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900 ${
          collapsed ? 'h-9 w-9 justify-center' : 'h-9 gap-2.5 px-2.5'
        } ${
          isActive
            ? 'bg-brand-800 font-600 text-white'
            : 'font-medium text-brand-100/75 hover:bg-brand-800/60 hover:text-white'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute inset-y-1.5 -left-3 w-1 rounded-full bg-gold-500" aria-hidden="true" />
          )}
          <Icon size={19} className="shrink-0" />
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );
};

const Sidebar = ({ collapsed, mobileOpen, onCloseMobile, onToggleCollapse }) => {
  return (
    <>
      {/* Mobile scrim */}
      <div
        className={`fixed inset-0 z-40 bg-brand-950/60 transition-opacity duration-200 lg:hidden ${
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onCloseMobile}
        aria-hidden="true"
      />

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-brand-900 transition-[transform,width] duration-200 ease-out lg:translate-x-0 ${
          collapsed ? 'w-[76px]' : 'w-64'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        aria-label="Admin navigation"
      >
        {/* Brand. The full wordmark is dark-on-light artwork, so it gets a white
            band at topbar height — the two line up into one header strip. The
            collapsed rail is too narrow for the wordmark, so it shows the coin. */}
        <div
          className={`flex h-20 shrink-0 items-center border-b border-ink-200 bg-white ${
            collapsed ? 'justify-center px-3' : 'gap-3 px-5'
          }`}
        >
          {collapsed ? (
            <img src="/logo.png" alt="ParcelBuddy" className="h-9 w-9 shrink-0 rounded-full" />
          ) : (
            <img src="/brand.png" alt="ParcelBuddy" className="h-11 w-auto" />
          )}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close navigation"
            className="ml-auto rounded-lg p-2 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 lg:hidden"
          >
            <IconClose size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className={`flex-1 overflow-y-auto py-3 ${collapsed ? 'px-3' : 'px-4'}`}>
          {navSections.map((section, index) => (
            <div key={section.label} className={index > 0 ? 'mt-3' : ''}>
              {!collapsed && (
                <p className="mb-1.5 px-2.5 text-[10px] font-700 uppercase tracking-[0.14em] text-brand-100/60">
                  {section.label}
                </p>
              )}
              {collapsed && index > 0 && <div className="mx-auto mb-2.5 h-px w-8 bg-white/10" />}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItem key={item.to} item={item} collapsed={collapsed} onNavigate={onCloseMobile} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer — just the collapse toggle. The signed-in admin's name/email
            and Log out already live in the topbar (Topbar.js); repeating
            them here was pure duplication for no second purpose. */}
        <div className={`shrink-0 border-t border-white/10 py-2.5 ${collapsed ? 'px-3' : 'px-4'}`}>
          <button
            type="button"
            onClick={onToggleCollapse}
            className={`hidden w-full items-center rounded-lg py-2 text-xs font-medium text-brand-100/70 transition-colors hover:bg-white/5 hover:text-white lg:flex ${
              collapsed ? 'justify-center' : 'gap-2 px-3'
            }`}
          >
            <IconPanelLeft size={15} />
            {!collapsed && 'Collapse'}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
